import {
  Box,
  Drawer,
  IconButton,
  ListItemText,
  MenuItem,
  Radio,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCinemas, useShowTimesDrawer } from '@/logic/state';
import {
  useGetShowTimesMoviesByDate,
  useGetShowtimesByOneCinema,
} from '@/logic/services';

import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { ILocale } from '@/lang/types';

import { checkIsRtl } from '@/lang/utils';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useCity } from '@/logic/hooks';
import { useGetCinemas } from '@/logic/services/cinemas';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import ImageBoxFuture from '../shared/image-box-future';
import DaysFilter from '../movie-details/showtimes/days-filter';
import { MovieCollapseList } from '../shared/movie-collapse-list';

const LocationIconBlue = '/icons/shared/location-icon-blue.svg';
const BodyImg = '/images/show-times/body.svg';
const BodyImg4k = '/images/show-times/body4k.svg';
const ShowTimes = '/icons/footer/showTimes.svg';
const HeaderImg = '/images/show-times/header.svg';
const HeaderImg4k = '/images/show-times/header4k.svg';

interface ShowTimesMovieParams {
  cityId: number;
  cinemaId: number;
  date: string;
}

export const ShowTimesDrawer: FC = () => {
  const { cityId } = useCity();
  const router = useRouter();
  const theme = useTheme();
  const intl = useIntl();
  const isRtl = checkIsRtl(router.locale as ILocale);

  const [showTimeDate, setShowTimeDate] = useState<string>('');
  const [drawerSelectedCinema, setDrawerSelectedCinema] = useState<number | null>(null);

  const [isShowTimesDrawerOpen, setShowTimesDrawer] = useShowTimesDrawer(
    s => [s.isShowTimesDrawerOpen, s.setShowTimesDrawer],
    shallow
  );
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('laptop'));
  const selectedCinemas = useCinemas(s => s.selectedCinemas, shallow);
  const matches = useMediaQuery('(min-height:1000px)');
  const queryClient = useQueryClient();

  const { data: cinemaData } = useGetCinemas(
    {
      params: {
        cityId,
      },
    },
    {
      enabled: !!cityId,
    }
  );

  useEffect(() => {
    if (!cinemaData) return;
    if (selectedCinemas.length === 0 || selectedCinemas[0] === 0) {
      setDrawerSelectedCinema(cinemaData?.cinema[0]?.id);
    } else {
      setDrawerSelectedCinema(selectedCinemas[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCinemas, cinemaData]);

  const { data: sessionDates, isFetching: isFetchingShowTimes } =
    useGetShowtimesByOneCinema(
      {
        params: {
          cityId,
          cinemaId: drawerSelectedCinema,
        },
      },
      {
        enabled: isShowTimesDrawerOpen && !!cityId && !!drawerSelectedCinema,
      }
    );

  const params: ShowTimesMovieParams = useMemo(
    () => ({
      cityId,
      cinemaId: drawerSelectedCinema,
      date: showTimeDate,
      page: 1,
      limit: 4,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [drawerSelectedCinema, showTimeDate]
  );

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useGetShowTimesMoviesByDate(
      {
        params: params,
      },
      { enabled: !!drawerSelectedCinema && !!showTimeDate && !!cityId }
    );

  const movies = useMemo(
    () => (data?.pages ? data?.pages?.flatMap(group => group.data) : []),
    [data]
  );

  const handleChange = (event: SelectChangeEvent<typeof drawerSelectedCinema>): void => {
    const {
      target: { value },
    } = event;
    setDrawerSelectedCinema(value as number);
    setShowTimeDate(null);
  };

  const callOnFilmHasBeenStarted = (): void => {
    // invalidate available dates
    queryClient.invalidateQueries([
      {
        method: 'get',
        url: 'showtimes',
        params: {
          cityId,
          cinemaId: drawerSelectedCinema || selectedCinemas,
        },
      },
    ]);
    // invalidate movies
    queryClient.invalidateQueries(['showtimes-drawer-movies', 'infinite', params]);
  };

  useEffect(() => {
    setShowTimesDrawer(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  return (
    <Drawer
      anchor='right'
      open={isShowTimesDrawerOpen}
      onClose={() => setShowTimesDrawer(false)}
      PaperProps={{
        sx: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          overflow: 'hidden',
          width: isMobileScreen ? '100vw' : '50vw',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          height: '100vh',
          background: {
            mobile: theme.palette.common.white,
            tablet: `url(${BodyImg}) no-repeat`,
            extraLargeDesktop: `url(${BodyImg4k}) no-repeat`,
          },
          backgroundSize: matches ? 'cover !important' : '100%',
          transform: isRtl && 'scaleX(-1)',
        }}
      >
        <Box
          sx={{
            width: {
              mobile: '100vw',

              tablet: '100%',
            },
            maxWidth: {
              mobile: 'none',
            },
            height: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: isRtl ? 'row-reverse' : 'row',
              justifyContent: 'flex-start',
              alignItems: 'center',
              height: {
                mobile: '56px',
                tablet: '100px',
              },
              width: '100%',
              background: {
                mobile: theme.palette.primary.main,
                tablet: `url(${HeaderImg}) no-repeat`,
                extraLargeDesktop: `url(${HeaderImg4k}) no-repeat`,
              },
              backgroundSize: '100%',
            }}
          >
            <IconButton onClick={() => setShowTimesDrawer(false)}>
              <CloseIcon
                sx={{
                  color: theme.palette.common.white,
                  width: 24,
                  height: 24,
                  marginInlineStart: {
                    mobile: !isRtl && '16px',
                    tablet: !isRtl && '28px',
                  },
                  marginInlineEnd: {
                    mobile: isRtl && '16px',
                    tablet: isRtl && '28px',
                  },
                }}
              />
            </IconButton>

            <ImageBoxFuture
              img={ShowTimes}
              title={intl.formatMessage({
                id: 'images.title.show_time',
              })}
              objectFit='contain'
              sx={{
                width: '24px',
                height: '24px',
                marginInlineStart: {
                  mobile: !isRtl ? '32px' : '14px',
                  tablet: !isRtl ? '70px' : '14px',
                },
                marginInlineEnd: {
                  mobile: '14px',
                  tablet: !isRtl ? '14px' : '70px',
                },
              }}
            />

            <Typography
              component='span'
              sx={{
                color: theme.palette.common.white,
                fontSize: pxToRem(20),
                fontWeight: 400,
                transform: isRtl && 'scaleX(-1)',
              }}
            >
              <FormattedMessage id='show_times' />
            </Typography>
          </Box>
          <Box
            sx={{
              height: '100%',
              pr: {
                mobile: 0,
                tablet: !isRtl ? pxToRem(40) : pxToRem(160),
              },
              pl: {
                mobile: 0,
                tablet: !isRtl ? pxToRem(160) : pxToRem(40),
              },
              width: '100%',

              mr: isRtl ? 'auto' : 0,
              ml: !isRtl ? 'auto' : 0,

              [theme.breakpoints.down(1200)]: {
                pr: !isRtl ? pxToRem(40) : pxToRem(100),
                pl: !isRtl ? pxToRem(100) : pxToRem(40),
                width: '95%',
              },
              [theme.breakpoints.down(1100)]: {
                pr: !isRtl ? pxToRem(40) : pxToRem(50),
                pl: !isRtl ? pxToRem(50) : pxToRem(40),
                width: '90%',
              },
              [theme.breakpoints.down(640)]: {
                width: '100%',
                px: '0 !important',
              },

              marginTop: pxToRem(44),
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: isRtl ? 'flex-end' : 'flex-start',
                paddingInline: {
                  mobile: '16px',
                  tablet: 'none',
                },
              }}
            >
              <Select
                labelId='cinema-select-label'
                id='cinema-select'
                value={drawerSelectedCinema}
                onChange={(event: SelectChangeEvent<typeof drawerSelectedCinema>) =>
                  handleChange(event)
                }
                fullWidth
                displayEmpty
                sx={{
                  backgroundColor: theme.palette.lightGrey.main,
                  maxWidth: {
                    tablet: pxToRem(440),
                  },
                  border: 'none',
                  outline: 'none',
                  '.MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  transform: isRtl && 'scaleX(-1)',
                }}
                MenuProps={{
                  BackdropProps: {
                    sx: {
                      backgroundColor: 'transparent',
                      backdropFilter: 'none',
                    },
                  },
                  PaperProps: {
                    sx: {
                      boxShadow:
                        '0px 4px 4px rgba(0, 0, 0, 0.25), 0px 0.893452px 17.869px rgba(0, 0, 0, 0.160949)',
                      backdropFilter: 'blur(10px)',
                    },
                  },
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                }}
                renderValue={(drawerSelectedCinema: number) => {
                  return (
                    <Stack direction='row' sx={{ alignItems: 'center' }}>
                      <Box
                        sx={{
                          backgroundColor: theme.palette.common.white,
                          width: pxToRem(36),
                          height: pxToRem(36),
                          borderRadius: '9px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          marginInlineEnd: '16px',
                        }}
                      >
                        <ImageBoxFuture
                          img={LocationIconBlue}
                          title={intl.formatMessage({
                            id: 'images.title.location',
                          })}
                          objectFit='contain'
                          sx={{
                            width: '16px',
                            height: '16px',
                          }}
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontSize: pxToRem(20),
                          fontWeight: '400',
                          lineHeight: '30px',
                          color: theme.palette.grey[80],
                        }}
                      >
                        {
                          cinemaData?.cinema?.find(
                            cinema => cinema.id === drawerSelectedCinema
                          )?.name
                        }
                      </Typography>
                    </Stack>
                  );
                }}
                IconComponent={props => (
                  <Box
                    {...props}
                    sx={{ top: 'calc(50% - 0.7em) !important', right: '27px !important' }}
                  >
                    <ExpandMoreIcon
                      sx={{ color: theme.palette.typography.color.secondary }}
                    />
                  </Box>
                )}
              >
                {cinemaData?.cinema.map(cinema => (
                  <MenuItem key={cinema.id} value={cinema.id}>
                    <Radio checked={drawerSelectedCinema === cinema.id} />
                    <ListItemText primary={cinema.name} />
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box
              sx={{
                marginTop: pxToRem(34),
                width: {
                  mobile: '100%',
                  tablet: 'auto',
                },
                maxWidth: {
                  mobile: 'none',
                  tablet: pxToRem(650),
                },
              }}
            >
              {sessionDates && (
                <Box
                  sx={{
                    marginTop: {
                      mobile: pxToRem(40),
                    },
                    paddingX: 2,
                    transform: isRtl && 'scaleX(-1)',
                  }}
                >
                  <DaysFilter
                    sessionsDate={sessionDates?.avaliableDates}
                    showTimeDate={showTimeDate}
                    selectDate={setShowTimeDate}
                    returnFirstEnabledDate={date => setShowTimeDate(date.date)}
                  />
                </Box>
              )}
            </Box>
            {!isFetching &&
            !isFetchingShowTimes &&
            !isFetchingNextPage &&
            (movies?.length === 0 || sessionDates?.avaliableDates?.length === 0) ? (
              <>
                <Box
                  sx={{
                    marginTop: pxToRem(80),
                    marginBottom: pxToRem(24),
                    display: 'flex',
                    justifyContent: 'center',
                    transform: isRtl && 'scaleX(-1)',
                  }}
                >
                  <ImageBoxFuture
                    img='/icons/movie-details/no-showtimes-available.svg'
                    title={intl.formatMessage({
                      id: 'alt.no_show_time_available',
                    })}
                    objectFit='contain'
                    sx={{
                      width: '300px',
                      height: '202px',
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    color: theme.palette.grey[50],
                    fontWeight: 400,
                    fontSize: pxToRem(20),
                    lineHeight: '30px',
                    textAlign: 'center',
                    transform: isRtl && 'scaleX(-1)',
                  }}
                >
                  <FormattedMessage id='movies.there.is.no.showtimes' />
                </Typography>
              </>
            ) : (
              <Box
                id='scrollableDiv'
                sx={{
                  direction: isRtl ? 'rtl' : 'ltr',
                  maxHeight: {
                    mobile: 'calc(100vh - 260px)',
                    tablet: 'calc(100vh - 360px)',
                  },
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  width: {
                    mobile: '100%',
                    tablet: '100%',
                  },
                  marginTop: {
                    mobile: pxToRem(40),
                    tablet: pxToRem(54),
                  },
                }}
              >
                <Box
                  sx={{
                    transform: isRtl && 'scaleX(-1)',
                    direction: 'ltr',
                    marginBottom: pxToRem(60),
                  }}
                >
                  <MovieCollapseList
                    movies={movies}
                    hasMore={hasNextPage}
                    fetchMovies={fetchNextPage}
                    isFetching={isFetching || isFetchingShowTimes}
                    isFetchingNextPage={isFetchingNextPage}
                    showExperiencesBy='film'
                    infiniteScrollProps={{
                      scrollThreshold: '200px',
                      style: {
                        overflow: 'unset',
                      },
                      scrollableTarget: 'scrollableDiv',
                    }}
                    callOnFilmHasBeenStarted={callOnFilmHasBeenStarted}
                    showTimeDate={showTimeDate}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default ShowTimesDrawer;
